import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import cn from 'classnames';
import { HoverCardWrapper } from 'components/common/HoverCardWrapper';
import { DEFAULT_THUMBNAIL_CARD_SIZE } from 'components/common/graphicUploader/styles';
import { ProcessIcon } from 'components/common/ProcessIcon';
import { ProcessIconSize } from 'components/common/ProcessIcon/types';
import { MemoIssueStage } from 'components/general/IssueStage';
import { IssueStageSize } from 'components/general/IssueStage/types';
import useIsMobile from 'hooks/useIsMobile';
import { MemoIssueCardChip } from 'components/inspection/IssueCardPreview/Chip';
import { inspectionPreviewCardSx } from 'components/inspection/InspectionPreviewCard/inspectionPreviewCardStyles';
import {
  MouseEvent,
  ReactElement,
  SyntheticEvent,
  TouchEvent,
  useCallback,
} from 'react';
import { IssueModel } from 'shared/domain/issue/issueModel';
import { Color } from 'shared/utils/colors';
import { IssueCardDetails, IssueCardDetailsProps } from './details';
import { useStyles } from './styles';

const stopPropagation = (e: SyntheticEvent): void => e.stopPropagation();

const TITLE_MAX_LINES = 2;
const LAST_EDIT_MAX_LINES = 2;

const squareCardStyle = { pointerEvents: 'all', margin: 0 } as const;

const squareCardSx = {
  width: DEFAULT_THUMBNAIL_CARD_SIZE,
  height: DEFAULT_THUMBNAIL_CARD_SIZE,
  overflow: 'hidden',
  boxSizing: 'border-box',
} satisfies SxProps<Theme>;

const squareActionAreaSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  // Same inset on every side, so the header and the stage footer sit at equal
  // distances from the tile edges.
  p: 1.5,
  gap: 0.75,
} satisfies SxProps<Theme>;

const squareHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  minWidth: 0,
} satisfies SxProps<Theme>;

const squareTitleSx = {
  fontFamily: 'Archivo',
  fontWeight: 600,
  fontSize: '1rem',
  lineHeight: 1.3,
  overflow: 'hidden',
  wordBreak: 'break-word',
  display: '-webkit-box',
  WebkitLineClamp: TITLE_MAX_LINES,
  WebkitBoxOrient: 'vertical',
} satisfies SxProps<Theme>;

const squareLastEditSx = {
  fontFamily: 'Archivo',
  fontSize: '0.75rem',
  lineHeight: 1.3,
  color: 'text.secondary',
  overflow: 'hidden',
  wordBreak: 'break-word',
  display: '-webkit-box',
  WebkitLineClamp: LAST_EDIT_MAX_LINES,
  WebkitBoxOrient: 'vertical',
} satisfies SxProps<Theme>;

const squareFooterSx = {
  mt: 'auto',
  display: 'flex',
  minWidth: 0,
} satisfies SxProps<Theme>;

const previewTitleSx = {
  fontFamily: 'Archivo',
  fontWeight: 700,
  fontSize: 16,
  lineHeight: 1.3,
  wordBreak: 'break-word',
} satisfies SxProps<Theme>;

interface IssueCardSquareProps extends IssueCardDetailsProps {
  processCode: string;
  processColor: Color;
  title: string;
  issueModel: IssueModel;
  onClick?: (issue: IssueModel) => void;
}

/** Everything the wide card shows once expanded, in an inspection-style card. */
function IssueCardPreview({
  processCode,
  processColor,
  title,
  ...detailsProps
}: Omit<IssueCardSquareProps, 'issueModel' | 'onClick'>): ReactElement {
  return (
    // React events bubble through the Popper portal: keep clicks inside the
    // preview from reaching the tile (which would open the issue).
    <Card
      sx={inspectionPreviewCardSx.base}
      raised
      onClick={stopPropagation}
      onTouchEnd={stopPropagation}
      data-qa='issue-card-preview'
    >
      <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, pb: 0 }}>
        <ProcessIcon
          code={processCode}
          color={processColor}
          size={ProcessIconSize.issueCard}
        />
        <Typography component='div' sx={previewTitleSx}>
          {title}
        </Typography>
      </CardContent>
      <Divider sx={{ my: 1 }} />
      <IssueCardDetails {...detailsProps} isExpanded />
    </Card>
  );
}

/**
 * Attachment-sized (182×182) issue tile. Hovering (or focusing) the tile shows
 * the details in a hover card instead of growing the tile. On mobile the first
 * tap opens the preview and the second opens the issue.
 */
export function IssueCardSquare(props: IssueCardSquareProps): ReactElement {
  const {
    processCode,
    processColor,
    title,
    hashtag,
    deleted,
    isSelected,
    issueModel,
    onClick,
    lastEditString,
    stageId,
    workflowId,
  } = props;
  const cs = useStyles();
  const isMobile = useIsMobile();
  const deletedClassName = (deleted && cs.deleted) || '';

  const openIssue = useCallback(() => {
    if (typeof onClick === 'function') onClick(issueModel);
  }, [onClick, issueModel]);

  const onCardClick = useCallback(
    (e: TouchEvent | MouseEvent) => {
      //ensures proper behavior of clickAwayListener in Documentation pins and areas
      e.stopPropagation();
      openIssue();
    },
    [openIssue]
  );

  return (
    <HoverCardWrapper
      // The preview is a neutral view: the selection outline stays on the tile.
      onHoverComponent={<IssueCardPreview {...props} isSelected={false} />}
      onSecondTap={openIssue}
      openOnFocus
    >
      <Card
        className={cn(cs.card, isSelected ? 'selected' : '')}
        variant='outlined'
        sx={squareCardSx}
        // `cs.card` adds a 3px side margin for the wide card; the tile keeps the
        // exact attachment-tile footprint.
        style={squareCardStyle}
        data-qa='issue-card-square'
      >
        <CardActionArea
          onTouchEnd={stopPropagation}
          // Mobile: HoverCardWrapper turns the first tap into "show preview"
          // and the second into `onSecondTap`.
          onClick={isMobile ? undefined : onCardClick}
          sx={squareActionAreaSx}
        >
          <Box sx={squareHeaderSx} className={deletedClassName}>
            <ProcessIcon
              code={processCode}
              color={processColor}
              size={ProcessIconSize.issueCard}
            />
            <MemoIssueCardChip values={[`#${hashtag}`]} />
          </Box>
          <Box sx={squareTitleSx} className={deletedClassName}>
            {title}
          </Box>
          <Box sx={squareLastEditSx} className={deletedClassName}>
            {lastEditString}
          </Box>
          <Box sx={squareFooterSx} className={deletedClassName}>
            <MemoIssueStage
              stageId={stageId}
              workflowId={workflowId}
              issueArchived={deleted}
              size={IssueStageSize.issueCard}
            />
          </Box>
        </CardActionArea>
      </Card>
    </HoverCardWrapper>
  );
}
